/**
 * SITE LOCALES — the read-side contract for a multilingual published site.
 *
 * Design: `docs/pm/active/cms-multilingual.md`. The short version, because it
 * decides what belongs here:
 *
 *   - The DEFAULT locale keeps today's URLs. Every other locale is a full copy of
 *     the published tree under a path prefix (`/zh/about`), written by the publish
 *     from the same pages with their strings swapped. So a site never decides
 *     which language to render — the payload it was handed already IS one.
 *   - `PublishedLocale` is stamped onto that payload. It is what tells the router
 *     its basename, the content loader which `content.json` to fetch, the switcher
 *     which languages exist, and the UI-string dictionary which column to read.
 *
 * ⚠️ ABSENT MEANS "SINGLE-LANGUAGE SITE", and every consumer must treat it so.
 * Every payload published before this existed has no `locale`, and an org with no
 * translations still publishes none — that is what keeps the feature dormant.
 */
/** The router basename for a payload: `/zh`, or `''` on a default/unlocalized tree. */
export function localeBasename(locale) {
    return locale?.prefix ? `/${locale.prefix}` : '';
}
/**
 * The same route in another locale's tree.
 *
 * `path` is a route path as the ROUTER sees it — basename already stripped, so
 * `/about` on `/zh/about`. Query and hash survive.
 */
export function pathInLocale(path, target) {
    const clean = path.startsWith('/') ? path : `/${path}`;
    if (!target.prefix)
        return clean;
    // `/` → `/zh`, not `/zh/` — the router strips trailing slashes, and the route
    // manifest key for a locale's home is the bare prefix.
    if (clean === '/' || clean.startsWith('/?') || clean.startsWith('/#'))
        return `/${target.prefix}${clean.slice(1)}`;
    return `/${target.prefix}${clean}`;
}
/**
 * Prefix an internal href with the current tree's basename.
 *
 * For links CODE builds (the logo's `/`, `/faculty/${id}`) — links that come from
 * DATA were already rewritten by the publish, which is why this refuses to
 * double-prefix. Anything that is not a root-relative path (`https:`, `mailto:`,
 * `#team`, protocol-relative `//cdn…`) is returned unchanged.
 */
export function localizeHref(href, locale) {
    const base = localeBasename(locale);
    if (!base || !href.startsWith('/') || href.startsWith('//'))
        return href;
    if (href === base || href.startsWith(`${base}/`) || href.startsWith(`${base}?`) || href.startsWith(`${base}#`)) {
        return href;
    }
    return pathInLocale(href, { prefix: locale.prefix });
}
/**
 * The inverse of `localizeHref`, for react-router's `<Link to>`.
 *
 * `<Link>` resolves `to` against the router basename, so handing it a href the
 * publish already prefixed (`/zh/about`) would navigate to `/zh/zh/about`.
 * This strips the current tree's prefix when present and leaves anything else
 * as it is.
 */
export function routerPath(href, locale) {
    const base = localeBasename(locale);
    if (!base || !href.startsWith(base))
        return href;
    const rest = href.slice(base.length);
    if (rest === '')
        return '/';
    if (rest.startsWith('/'))
        return rest;
    if (rest.startsWith('?') || rest.startsWith('#'))
        return `/${rest}`;
    return href; // `/zhuhai` — a different path that merely shares the letters
}
